import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { Button } from './Button';
import { Lock, Loader2, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

export const PasswordResetView: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
        setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
        setLoading(false);
        return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: password });
      if (error) throw error;
      setSuccess(true);
      // Redirect to home after a short delay
      setTimeout(() => {
          window.location.href = '/';
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Zurücksetzen des Passworts.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-950 font-sans">
            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-2xl max-w-md w-full text-center animate-in fade-in zoom-in duration-500">
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Passwort geändert!</h2>
                <p className="text-slate-400 mb-6">Ihr Passwort wurde erfolgreich aktualisiert. Sie werden in Kürze weitergeleitet...</p>
                <Button variant="primary" onClick={() => window.location.href = '/'} className="w-full bg-blue-600 hover:bg-blue-500">
                    Zum Dashboard
                </Button>
            </div>
        </div>
      );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-slate-950 font-sans">
      {/* Background Video (Same as AuthScreen) */}
      <video 
        autoPlay 
        muted 
        loop 
        playsInline 
        className="absolute inset-0 w-full h-full object-cover z-0 opacity-50"
      >
        <source src="https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/homepage_GefAS.mp4" type="video/mp4" />
      </video>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 p-8 rounded-2xl shadow-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black text-white tracking-tighter mb-2">Neues Passwort</h1>
              <p className="text-slate-400 text-sm">Bitte vergeben Sie ein neues Passwort für Ihren Account.</p>
            </div>

            <form onSubmit={handlePasswordReset} className="space-y-5">
              {error && (
                <div className="p-4 bg-red-500/20 text-red-400 border border-red-500/30 text-xs rounded-xl flex items-start">
                    <AlertCircle className="w-4 h-4 mr-3 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <span className="font-bold block mb-0.5 uppercase tracking-wider">Fehler</span>
                        <span className="leading-relaxed opacity-90">{error}</span>
                    </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-white uppercase tracking-widest ml-1">Neues Passwort</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3 bg-slate-900/60 border border-slate-700/50 text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-slate-500 text-sm"
                    placeholder="••••••••"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-white uppercase tracking-widest ml-1">Passwort bestätigen</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-900/60 border border-slate-700/50 text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-slate-500 text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading} 
                className="w-full py-4 mt-4 text-sm font-bold uppercase tracking-[0.2em] bg-blue-600 hover:bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-900/40 active:scale-[0.98] transition-all border-none"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort speichern'}
              </Button>
            </form>
        </div>
      </div>
    </div>
  );
};
